import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { formatDollars, itemLabel, type AdminInquiry, type InquiryStatus } from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { setAppTitle, STATUS_LABEL, timeAgo } from '../lib/format';
import { Icon } from '../lib/Icon';

const TABS: { status: InquiryStatus; label: string; caption: string }[] = [
  { status: 'NEW', label: 'New', caption: 'New buyers' },
  { status: 'HANDLED', label: 'Done', caption: 'Buyers you marked done' },
  { status: 'SPAM', label: 'Spam', caption: 'Spam' },
];

export function InquiriesPage() {
  const { saleId = '' } = useParams();
  const [tab, setTab] = useState<InquiryStatus>('NEW');
  const [inquiries, setInquiries] = useState<AdminInquiry[] | null>(null);
  const [newCount, setNewCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setAppTitle('My Buyers'), []);

  useEffect(() => {
    setInquiries(null);
    api.inquiries(saleId, tab).then(
      (r) => {
        setInquiries(r.inquiries);
        setNewCount(r.newCount);
      },
      (err: ApiError) => setError(err.message),
    );
  }, [saleId, tab]);

  async function move(inquiry: AdminInquiry, status: InquiryStatus) {
    try {
      await api.updateInquiry(inquiry.id, status);
      setInquiries((list) => list?.filter((i) => i.id !== inquiry.id) ?? null);
      setNewCount((n) => n + (status === 'NEW' ? 1 : 0) - (inquiry.status === 'NEW' ? 1 : 0));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update. Try again.');
    }
  }

  return (
    <main className="page narrow">
      <header className="admin-header">
        <h1>My Buyers</h1>
        <Link className="link small" to={`/admin/sales/${saleId}`}>Back to my sale</Link>
      </header>

      <div className="tabs underline" role="tablist">
        {TABS.map((t) => (
          <button key={t.status} role="tab" aria-selected={tab === t.status} className={tab === t.status ? 'on' : ''} onClick={() => setTab(t.status)}>
            {t.label}
            {t.status === 'NEW' && newCount > 0 && <span className="badge">{newCount}</span>}
          </button>
        ))}
      </div>
      <p className="list-caption">
        Showing: <strong>{TABS.find((t) => t.status === tab)!.caption}</strong>
        {inquiries && ` (${inquiries.length})`}
      </p>

      {error && <p className="error" role="alert">{error}</p>}
      {!inquiries ? (
        <p className="center muted">Loading…</p>
      ) : inquiries.length === 0 ? (
        <p className="empty muted">
          {tab === 'NEW' ? 'No new buyers yet. When someone taps "I want this", they show up here and you get an email.' : 'Nothing here.'}
        </p>
      ) : (
        <ul className="inquiry-list">
          {inquiries.map((inq) => (
            <li key={inq.id} className="inquiry card">
              <div className="inquiry-item">
                {inq.item.thumbUrl ? <img src={inq.item.thumbUrl} alt="" /> : <div className="noimg" />}
                <div>
                  <strong>{itemLabel(inq.item)}</strong>
                  <span className="muted small">
                    {inq.item.priceLabel}
                    {inq.item.status !== 'AVAILABLE' && ` · ${STATUS_LABEL[inq.item.status]}`}
                  </span>
                </div>
              </div>
              <p className="inquiry-who">
                <strong>{inq.name}</strong>{' '}
                {inq.kind === 'OFFER' && inq.offerCents !== null ? (
                  <>offers <strong className="offer">{formatDollars(inq.offerCents)}</strong></>
                ) : (
                  'wants it'
                )}
                <span className="muted small"> · {timeAgo(inq.createdAt)}</span>
              </p>
              {inq.message && <p className="prewrap inquiry-msg">{inq.message}</p>}
              <div className="actions">
                <a className="btn primary" href={`tel:${inq.phone.replace(/[^\d+]/g, '')}`}>
                  <Icon name="phone" size={18} /> Call
                </a>
                <a className="btn" href={`sms:${inq.phone.replace(/[^\d+]/g, '')}`}>
                  <Icon name="message" size={18} /> Text
                </a>
                {inq.email && <a className="btn" href={`mailto:${inq.email}?subject=${encodeURIComponent(itemLabel(inq.item))}`}><Icon name="mail" size={18} /> Email</a>}
              </div>
              <p className="muted small">{inq.phone}{inq.email ? ` · ${inq.email}` : ''}</p>
              <div className="actions">
                {inq.status !== 'HANDLED' && <button className="btn" onClick={() => move(inq, 'HANDLED')}>Done</button>}
                {inq.status !== 'NEW' && <button className="btn" onClick={() => move(inq, 'NEW')}>Move to New</button>}
              </div>
              <div className="inquiry-foot">
                <Link className="link small" to={`/admin/sales/${saleId}/items/${inq.item.id}`}>Edit item</Link>
                {/* Kept apart from the other buttons and confirmed, so a stray tap can't hide a real buyer. */}
                {inq.status !== 'SPAM' && (
                  <button className="link small muted" onClick={() => window.confirm(`Move ${inq.name} to Spam?`) && move(inq, 'SPAM')}>
                    Report spam
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
